/**
 * ============================================================================
 * NOTES OVERVIEW v4.0 - PLANIVIO BRAND REDESIGN
 * ============================================================================
 *
 * ✅ v4.0: KOMPLETT NEUES REDESIGN
 *          - KEIN eigener View-Toggle (kommt aus Notes.js)
 *          - CSS-Klassen statt Inline-Styles
 *          - Karten-View: 2-Spalten Post-it Grid (notes-grid)
 *          - Listen-View: Kompakt mit border-left (notes-list)
 *          - data-color Attribut für Farb-Zuordnung
 *          - data-category Attribut für Kategorie-Badge
 *          - Person/Zuweisungs-Anzeige beibehalten
 *
 * Basiert auf: planivio_notes_redesign_v3.html
 * @version 4.0.0 - Planivio Brand Redesign
 */

import React, { useMemo } from 'react';

console.log('📝 NotesOverview v4.0 loaded - PLANIVIO BRAND REDESIGN 🎨');

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES = {
  personal:  { name: 'Persönlich', icon: '🏠' },
  work:      { name: 'Arbeit',     icon: '💼' },
  ideas:     { name: 'Ideen',      icon: '💡' },
  important: { name: 'Wichtig',    icon: '⭐' }
};

// Kategorie → Fallback-Farbe wenn note.color nicht gesetzt
const CATEGORY_DEFAULT_COLOR = {
  personal:  'green',
  work:      'blue',
  ideas:     'purple',
  important: 'yellow'
};

// Icon-Hintergrund für Listen-View
const COLOR_BG = {
  yellow: '#FEF3C7',
  green:  '#D1FAE5',
  blue:   '#DBEAFE',
  pink:   '#FCE7F3',
  purple: '#EDE9FE',
  orange: '#FFEDD5'
};

// ============================================================================
// HELPERS
// ============================================================================

const getCategoryInfo = (categoryId) => {
  return CATEGORIES[categoryId] || { name: categoryId || 'Sonstige', icon: '📝' };
};

const getColorClass = (note) => {
  // Direkte Farbe aus note.color
  if (note.color && COLOR_BG[note.color]) {
    return `color-${note.color}`;
  }
  // Fallback: Kategorie → Farbe
  const fallback = CATEGORY_DEFAULT_COLOR[note.category] || 'yellow';
  return `color-${fallback}`;
};

const getColorKey = (note) => {
  if (note.color && COLOR_BG[note.color]) return note.color;
  return CATEGORY_DEFAULT_COLOR[note.category] || 'yellow';
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Min`;
  if (diffHours < 24) return `vor ${diffHours} Std`;
  if (diffDays < 7) return `vor ${diffDays} T`;

  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// ============================================================================
// COMPONENT
// ============================================================================

const NotesOverview = ({
  notes,
  onEditNote,
  searchQuery,
  filterCategory,
  viewMode = 'grid',
  setViewMode,      // empfangen aber NICHT verwendet (Toggle ist in Notes.js)
  memberOptions = [],
  t,
  isDark
}) => {

  // ============================================
  // FILTERING
  // ============================================
  const filteredNotes = useMemo(() => {
    return notes.filter(note => {
      const matchesSearch = !searchQuery ||
        note.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.content?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = filterCategory === 'all' || note.category === filterCategory;

      return matchesSearch && matchesCategory;
    });
  }, [notes, searchQuery, filterCategory]);

  // ============================================
  // MEMBER LOOKUP
  // ============================================
  const getMemberInfo = (personId) => {
    if (!personId || personId === 'alle') {
      return { name: 'Alle', avatar: '👥' };
    }
    const member = memberOptions.find(m =>
      String(m.id) === String(personId) ||
      String(m.value) === String(personId)
    );
    return member
      ? { name: member.name, avatar: member.avatar || '👤' }
      : { name: 'Unbekannt', avatar: '👤' };
  };

  // ============================================
  // RENDER - EMPTY STATE
  // ============================================
  if (filteredNotes.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">📝</div>
        <h3>
          {searchQuery || filterCategory !== 'all'
            ? 'Keine Notizen gefunden'
            : 'Noch keine Notizen vorhanden'}
        </h3>
        <p>
          {searchQuery || filterCategory !== 'all'
            ? 'Versuche andere Suchbegriffe'
            : 'Erstelle deine erste Notiz'}
        </p>
      </div>
    );
  }

  // ============================================
  // RENDER - CARD VIEW (Post-it Grid)
  // ============================================
  const renderGridView = () => (
    <div className="notes-grid">
      {filteredNotes.map((note, index) => {
        const category = getCategoryInfo(note.category);
        const colorClass = getColorClass(note);
        const memberInfo = getMemberInfo(note.person || note.assigned_to);

        return (
          <div
            key={note.id}
            className={`note-card ${colorClass}`}
            data-color={getColorKey(note)}
            data-category={note.category}
            onClick={() => onEditNote(note)}
          >
            {/* Title */}
            <div className="note-card-title">
              {note.title || 'Ohne Titel'}
            </div>

            {/* Content/Excerpt */}
            <div className="note-card-content">
              {note.content || ''}
            </div>

            {/* Footer: Category Tag + Meta */}
            <div className="note-card-footer">
              <span
                className="note-card-category"
                data-category={note.category}
              >
                {category.icon} {category.name}
              </span>

              <div className="note-card-meta">
                <span className="note-card-author">
                  <span className="note-card-author-dot" />
                  {memberInfo.avatar} {memberInfo.name}
                </span>
                <span className="note-card-date">
                  {formatDate(note.created_at)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ============================================
  // RENDER - LIST VIEW
  // ============================================
  const renderListView = () => (
    <div className="notes-list">
      {filteredNotes.map((note, index) => {
        const category = getCategoryInfo(note.category);
        const colorClass = getColorClass(note);
        const colorKey = getColorKey(note);
        const memberInfo = getMemberInfo(note.person || note.assigned_to);

        return (
          <div
            key={note.id}
            className={`note-card ${colorClass}`}
            data-color={colorKey}
            data-category={note.category}
            onClick={() => onEditNote(note)}
          >
            {/* Icon */}
            <div
              className="note-card-icon"
              style={{ background: COLOR_BG[colorKey] || '#FEF3C7' }}
            >
              📝
            </div>

            {/* Body */}
            <div className="note-card-body">
              <div className="note-card-title">
                {note.title || 'Ohne Titel'}
              </div>

              <div className="note-card-content">
                {truncateText(note.content, 80)}
              </div>

              {/* Footer in List */}
              <div className="note-card-footer">
                <span
                  className="note-card-category"
                  data-category={note.category}
                >
                  {category.icon} {category.name}
                </span>

                <div className="note-card-meta">
                  <span className="note-card-author">
                    <span className="note-card-author-dot" />
                    {memberInfo.avatar} {memberInfo.name}
                  </span>
                  <span className="note-card-date">
                    {formatDate(note.created_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ============================================
  // MAIN RENDER – KEIN View-Toggle hier!
  // ============================================
  return viewMode === 'grid' ? renderGridView() : renderListView();
};

export default NotesOverview;